/**
 * The name the account menu shows, read straight off Firebase rather than
 * `/control/me` -- `users` holds `firebase_uid` and `email` and no name
 * column (platform design measured basis), and `displayName` is the only
 * place a caller's typed name lives at all. Same guard as
 * `./token`'s `firebaseIdToken`: no default app is a normal state, not an
 * error.
 */

import { getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';

import type { AccountProvider } from '../../chrome/shell';

export function firebaseDisplayName(): string | null {
  if (getApps().length === 0) return null;
  return getAuth().currentUser?.displayName ?? null;
}

/** Firebase's own `displayName` first; a caller who signed up before it was
 *  captured, or through a flow that never sets one, still has an email. */
export function resolveDisplayName(
  firebaseName: string | null,
  email: string | null,
): string | null {
  return firebaseName ?? email;
}

/** Firebase's own provider ids for the two methods this platform offers. A
 *  third -- an account linked to a provider the sign-in screen never
 *  presented -- is no answer rather than a guess, because the account pane
 *  states how the caller got in and a wrong answer there is worse than
 *  none. */
export function providerKind(providerId: string | null): AccountProvider | null {
  if (providerId === 'google.com') return 'google';
  if (providerId === 'password') return 'password';
  return null;
}

export function firebaseProvider(): AccountProvider | null {
  if (getApps().length === 0) return null;
  return providerKind(getAuth().currentUser?.providerData[0]?.providerId ?? null);
}
